<template>
  <div class="massage-page">
    <app-cells>
      <h1 class="title">Massage Schedule</h1>
    </app-cells>

    <app-cells class="massage-page__filters">
      <app-form-group label="Property" required>
        <app-select
          v-model="selected_property_id"
          :options="property_options"
          :reduce="item => item.id"
          label="name"
          :clearable="false"
          placeholder="Select property"
        />
      </app-form-group>

      <app-form-group label="Date range" required>
        <app-date-picker
          v-model="selected_date_range"
          value-type="YYYY-MM-DD"
          format="DD MMM YYYY"
          placeholder="Select dates"
          range
          :clearable="false"
        />
      </app-form-group>

      <app-form-group label="Search guest">
        <app-input
          v-model.trim="search"
          type="search"
          placeholder="Name or phone"
          name="search"
        />
      </app-form-group>
    </app-cells>

    <div class="massage-board">
      <div v-if="isBoardLoading" class="massage-board__overlay">
        <img src="@/assets/img/preloader.svg" alt="Loading" class="massage-board__overlay-spinner">
      </div>

      <div class="massage-board__nav">
        <div class="massage-board__nav-side">
          <button type="button" class="massage-board__nav-btn" @click="goToPrevWeek">
            &lt; Prev
          </button>
          <button type="button" class="massage-board__nav-btn" @click="goToToday">
            Today
          </button>
        </div>
        <div class="massage-board__range">{{ weekRangeLabel }}</div>
        <div class="massage-board__nav-side massage-board__nav-side--right">
          <button type="button" class="massage-board__nav-btn" @click="goToNextWeek">
            Next &gt;
          </button>
        </div>
      </div>

      <div v-if="is_schedule_loading" class="massage-board__placeholder">
        Loading schedule...
      </div>

      <div v-else-if="is_schedule_loaded && !slots.length" class="massage-board__placeholder">
        {{ schedule_error || 'Schedule not found for selected property and date' }}
      </div>

      <template v-else>
        <div class="massage-calendar">
          <div class="massage-calendar__head" :style="calendarGridStyle">
            <div class="massage-calendar__time-head">Time</div>
            <div
              v-for="day in weekDays"
              :key="day.date"
              class="massage-calendar__day-head"
              :class="{ 'massage-calendar__day-head--today': day.is_today }"
            >
              {{ day.label }}
            </div>
          </div>

          <div
            v-for="slot in slots"
            :key="slot.code"
            class="massage-calendar__row"
            :style="calendarGridStyle"
          >
            <div class="massage-calendar__time">{{ slot.from_label }}</div>
            <div
              v-for="day in weekDays"
              :key="`${slot.code}-${day.date}`"
              class="massage-calendar__cell"
              :class="{ 'massage-calendar__cell--today': day.is_today }"
            >
              <div class="massage-calendar__cell-actions">
                <div
                  v-if="getSlotBlock(day.date, slot.code)"
                  class="massage-calendar__slot-wrap"
                >
                  <button
                    type="button"
                    class="massage-calendar__blocked"
                    @click.stop="toggleBlockPopover(getSlotBlock(day.date, slot.code))"
                  >
                    <span></span>
                  </button>

                  <div
                    v-if="isBlockPopoverActive(getSlotBlock(day.date, slot.code))"
                    class="massage-popover massage-popover--block"
                    @click.stop
                  >
                    <div class="massage-popover__section">
                      <div class="massage-popover__source">Blocked</div>
                      <div v-if="getSlotBlock(day.date, slot.code).notes" class="massage-popover__block-notes">
                        {{ getSlotBlock(day.date, slot.code).notes }}
                      </div>
                      <div v-if="getSlotBlock(day.date, slot.code).created_by_name" class="massage-popover__meta">
                        <span>Blocked by {{ getSlotBlock(day.date, slot.code).created_by_name }}</span>
                      </div>
                    </div>
                    <button
                      v-if="can_manage_slot_blocks"
                      type="button"
                      class="massage-popover__action massage-popover__action--danger"
                      :disabled="is_block_submitting"
                      @click.stop="deleteSlotBlock(getSlotBlock(day.date, slot.code))"
                    >
                      Unblock
                    </button>
                  </div>
                </div>

                <template v-else>
                  <div
                  v-for="booking in getVisibleSlotBookings(day.date, slot.code)"
                  :key="booking.id"
                  :style="{ order: booking.therapist_position }"
                  class="massage-calendar__slot-wrap"
                >
                  <button
                    type="button"
                    class="massage-calendar__booking"
                    :disabled="isDialogOpening"
                    @click.stop="toggleBookingPopover(booking)"
                  >
                    <img
                      v-if="isEditButtonLoading(booking.id)"
                      src="@/assets/img/preloader.svg"
                      alt="Loading"
                      class="massage-calendar__spinner"
                    >
                    <template v-else>
                      <span
                        v-for="group in getBookingCalendarGroups(booking)"
                        :key="group.key"
                        class="massage-calendar__booking-group"
                        :class="{ 'massage-calendar__booking-group--couple': group.is_couple }"
                      >
                        <span
                          v-for="(participant, index) in group.participants"
                          :key="`${group.key}-${index}`"
                          class="massage-calendar__guest"
                          :class="getParticipantGenderClass(participant)"
                        >
                          <img
                            v-if="getParticipantIconSrc(participant)"
                            :src="getParticipantIconSrc(participant)"
                            :alt="getParticipantInfoLabel(participant)"
                            class="massage-calendar__guest-icon"
                          >
                          <span v-else class="massage-calendar__guest-text">?</span>
                        </span>
                      </span>
                    </template>
                  </button>

                  <div
                    v-if="isBookingPopoverActive(booking)"
                    class="massage-popover"
                    @click.stop
                  >
                    <div
                      v-for="group in booking.room_groups"
                      :key="`room-${booking.id}-${group.id || group.reservation_room_id}`"
                      class="massage-popover__section"
                    >
                      <div class="massage-popover__source-row">
                        <div class="massage-popover__source">Hotel Guest</div>
                        <div v-if="group.is_couple" class="massage-popover__couple">Couple</div>
                      </div>
                      <div class="massage-popover__room">{{ group.room_short_name || 'Room not assigned' }}</div>
                      <div
                        v-for="guest in group.guests"
                        :key="`guest-${booking.id}-${guest.guest_person_id}`"
                        class="massage-popover__guest"
                      >
                        <span class="massage-popover__icon" :class="getParticipantGenderClass(guest)">
                          <img
                            v-if="getParticipantIconSrc(guest)"
                            :src="getParticipantIconSrc(guest)"
                            :alt="getParticipantInfoLabel(guest)"
                          >
                          <span v-else>?</span>
                        </span>
                        <span class="massage-popover__guest-name">{{ guest.full_name }}</span>
                        <span class="massage-popover__info">{{ getParticipantInfoLabel(guest) }}</span>
                      </div>
                    </div>

                    <div
                      v-for="guest in booking.walk_in_guests"
                      :key="`walk-in-${booking.id}-${guest.id || guest.full_name}`"
                      class="massage-popover__section"
                    >
                      <div class="massage-popover__source">Walk-in</div>
                      <div class="massage-popover__guest massage-popover__guest--walk-in">
                        <span class="massage-popover__icon" :class="getParticipantGenderClass(guest)">
                          <img
                            v-if="getParticipantIconSrc(guest)"
                            :src="getParticipantIconSrc(guest)"
                            :alt="getParticipantInfoLabel(guest)"
                          >
                          <span v-else>?</span>
                        </span>
                        <span class="massage-popover__guest-name">{{ getWalkInGuestName(guest) }}</span>
                        <span class="massage-popover__info">{{ getParticipantInfoLabel(guest) }}</span>
                      </div>
                      <div class="massage-popover__meta">
                        <span>{{ guest.phone || '-' }}</span>
                      </div>
                    </div>

                    <div
                      v-if="booking.created_audit || booking.updated_audit"
                      class="massage-popover__section"
                    >
                      <div class="massage-popover__meta">
                        <span v-if="booking.created_audit">
                          Created: {{ booking.created_audit.name }}
                          <span
                            v-if="booking.created_audit.datetime"
                            class="massage-popover__clock"
                            role="img"
                            :title="booking.created_audit.datetime"
                            :aria-label="booking.created_audit.datetime"
                          >🕒</span>
                        </span>
                        <span v-if="booking.updated_audit">
                          Updated: {{ booking.updated_audit.name }}
                          <span
                            v-if="booking.updated_audit.datetime"
                            class="massage-popover__clock"
                            role="img"
                            :title="booking.updated_audit.datetime"
                            :aria-label="booking.updated_audit.datetime"
                          >🕒</span>
                        </span>
                      </div>
                    </div>

                    <button
                      v-if="!is_therapist"
                      type="button"
                      class="massage-popover__action"
                      @click.stop="openEditFromPopover(booking)"
                    >
                      Edit
                    </button>
                  </div>
                  </div>

                  <div
                  v-for="emptyIndex in getAvailableTherapistPositions(day.date, slot.code)"
                  :key="`empty-${day.date}-${slot.code}-${emptyIndex}`"
                  :style="{ order: emptyIndex }"
                  class="massage-calendar__slot-wrap"
                >
                  <button
                    type="button"
                    class="massage-calendar__empty"
                    :disabled="isDialogOpening"
                    @click.stop="toggleEmptyPopover(day.date, slot.code, emptyIndex)"
                  >
                    <img
                      v-if="isCreateButtonLoading(day.date, slot.code)"
                      src="@/assets/img/preloader.svg"
                      alt="Loading"
                      class="massage-calendar__spinner"
                    >
                    <span v-else></span>
                  </button>

                  <div
                    v-if="isEmptyPopoverActive(day.date, slot.code, emptyIndex)"
                    class="massage-popover massage-popover--empty"
                    @click.stop
                  >
                    <div class="massage-popover__section">
                      <div class="massage-popover__source">Free</div>
                      <div class="massage-popover__empty-title">No records yet</div>
                      <div class="massage-popover__empty-note">Info: Free</div>
                    </div>
                    <button
                      v-if="!is_therapist"
                      type="button"
                      class="massage-popover__action"
                      @click.stop="openCreateFromPopover(day.date, slot.code, emptyIndex)"
                    >
                      Add
                    </button>
                    <button
                      v-if="can_manage_slot_blocks && !getSlotBookings(day.date, slot.code).length"
                      type="button"
                      class="massage-popover__action massage-popover__action--danger"
                      @click.stop="openBlockDialog(day.date, slot.code)"
                    >
                      Block
                    </button>
                  </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <div class="massage-legend">
          <div class="massage-legend__item">
            <span class="massage-calendar__guest massage-calendar__guest--male">
              <img
                :src="getParticipantIconSrc({ gender: 'male' })"
                alt="Male"
                class="massage-calendar__guest-icon"
              >
            </span>
            <span>Male</span>
          </div>
          <div class="massage-legend__item">
            <span class="massage-calendar__guest massage-calendar__guest--female">
              <img
                :src="getParticipantIconSrc({ gender: 'female' })"
                alt="Female"
                class="massage-calendar__guest-icon"
              >
            </span>
            <span>Female</span>
          </div>
          <div class="massage-legend__item">
            <span class="massage-calendar__booking-group massage-calendar__booking-group--couple">
              <span class="massage-calendar__guest massage-calendar__guest--male">
                <img
                  :src="getParticipantIconSrc({ gender: 'male' })"
                  alt="Male"
                  class="massage-calendar__guest-icon"
                >
              </span>
              <span class="massage-calendar__guest massage-calendar__guest--female">
                <img
                  :src="getParticipantIconSrc({ gender: 'female' })"
                  alt="Female"
                  class="massage-calendar__guest-icon"
                >
              </span>
            </span>
            <span>Couple</span>
          </div>
          <div class="massage-legend__item">
            <span class="massage-calendar__guest massage-calendar__guest--free">
              ?
            </span>
            <span>Not specified</span>
          </div>
          <div class="massage-legend__item">
            <span class="massage-calendar__empty massage-legend__empty">
              <span></span>
            </span>
            <span>Free</span>
          </div>
          <div class="massage-legend__item">
            <span class="massage-calendar__blocked massage-legend__blocked">
              <span></span>
            </span>
            <span>Blocked</span>
          </div>
        </div>
      </template>
    </div>

    <modal
      name="massage-booking-modal"
      :width="620"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup massage-dialog">
        <button class="popup__close" type="button" @click="closeDialog"></button>
        <div class="popup__title">
          {{ dialog.mode === 'create' ? 'Create Booking' : 'Edit Booking' }}
        </div>

        <div class="popup__body">
            <div v-if="dialog.mode === 'create'" class="massage-dialog__context">
              <div class="massage-dialog__context-item">
                <span>Date:</span>
                <strong>{{ formatBookingDate(dialog.form.booking_date) }}</strong>
              </div>
              <div class="massage-dialog__context-item">
                <span>Slot:</span>
                <strong>{{ dialog.form.slot_code }}</strong>
            </div>
          </div>
          <div v-else class="massage-dialog__grid">
            <app-form-group label="Booking date" required>
              <app-date-picker
                v-model="dialog.form.booking_date"
                @input="onDialogBookingDateChange"
                value-type="YYYY-MM-DD"
                format="DD MMM YYYY"
                placeholder="Booking date"
              />
              <template #error>
                <div v-if="$v.dialog.form.booking_date.$dirty && !$v.dialog.form.booking_date.required">
                  Required field
                </div>
              </template>
            </app-form-group>

            <app-form-group label="Slot" required>
              <app-select
                v-model="dialog.form.slot_code"
                :options="slot_options"
                :reduce="item => item.code"
                label="code"
                :clearable="false"
                placeholder="Select slot"
                :error="$v.dialog.form.slot_code.$error"
                @input="$v.dialog.form.slot_code.$touch()"
              />
              <template #error>
                <div v-if="$v.dialog.form.slot_code.$dirty && !$v.dialog.form.slot_code.required">
                  Required field
                </div>
              </template>
            </app-form-group>
          </div>

          <div class="massage-dialog__grid">
            <app-form-group label="Therapist" required>
              <div class="massage-segment">
                <button
                  v-for="position in [1, 2]"
                  :key="position"
                  type="button"
                  class="massage-segment__btn"
                  :class="{ 'massage-segment__btn--active': dialog.form.therapist_position === position }"
                  @click="dialog.form.therapist_position = position"
                >
                  {{ position === 1 ? 'Left' : 'Right' }}
                </button>
              </div>
              <small>Two participants book both therapists.</small>
            </app-form-group>

            <app-form-group label="Source" required>
              <app-select
                v-model="dialog.form.source_type"
                :options="source_type_options"
                :reduce="item => item.id"
                label="name"
                :clearable="false"
                placeholder="Source type"
                :error="$v.dialog.form.source_type.$error"
                @input="onDialogSourceTypeChange(); $v.dialog.form.source_type.$touch()"
              />
              <template #error>
                <div v-if="$v.dialog.form.source_type.$dirty && !$v.dialog.form.source_type.required">
                  Required field
                </div>
              </template>
            </app-form-group>

            <app-form-group v-if="dialog.form.source_type === 'walk_in'" label="Participant Count" required>
              <div class="massage-segment">
                <button
                  v-for="count in 2"
                  :key="count"
                  type="button"
                  class="massage-segment__btn"
                  :class="{ 'massage-segment__btn--active': dialog.form.participant_count === count }"
                  @click="setParticipantCount(count); $v.dialog.form.participant_count.$touch()"
                >
                  {{ count }}
                </button>
              </div>
              <template #error>
                <div v-if="$v.dialog.form.participant_count.$dirty && !$v.dialog.form.participant_count.required">
                  Required field
                </div>
              </template>
            </app-form-group>
          </div>

          <p v-if="getDialogCapacityError()" class="text-danger">{{ getDialogCapacityError() }}</p>

          <app-form-group v-if="dialog.form.source_type === 'existing_guest'" label="Room" required>
            <app-select
              v-model="dialog.form.reservation_room_id"
              :options="reservation_rooms"
              :reduce="item => item.id"
              label="room_option_label"
              :display-field="roomOptionLabel"
              :filterable="true"
              :clearable="false"
              :loading="is_reservation_rooms_loading"
              placeholder="Select room"
              :error="$v.dialog.form.reservation_room_id.$error"
              @input="onDialogReservationRoomChange(); $v.dialog.form.reservation_room_id.$touch()"
            />
            <template #error>
              <div v-if="$v.dialog.form.reservation_room_id.$dirty && !$v.dialog.form.reservation_room_id.required">
                Required field
              </div>
            </template>
          </app-form-group>

          <app-form-group v-if="dialog.form.source_type === 'existing_guest'" label="Guests" required>
            <app-select
              v-model="selectedExistingGuestIds"
              :options="selectedReservationRoomGuests"
              :reduce="item => item.id"
              label="full_name"
              :display-field="guestOptionLabelByRoom"
              :filterable="true"
              :disabled="!dialog.form.reservation_room_id"
              :closeOnSelect="false"
              :selectable="canSelectExistingGuest"
              multiple
              placeholder="Select guests"
              @input="$v.dialog.form.participants.$touch()"
            />
            <template #error>
              <div v-if="$v.dialog.form.participants.$dirty && !$v.dialog.form.participants.requiredForSource">
                Required field
              </div>
            </template>
          </app-form-group>

          <template v-if="dialog.form.source_type === 'walk_in'">
            <div
              v-for="(participant, index) in dialog.form.participants"
              :key="`participant-${index}`"
              class="massage-participant"
            >
              <h3 class="massage-participant__title">Participant {{ index + 1 }}</h3>
              <div class="massage-dialog__grid">
                <app-form-group label="First name" required>
                  <app-input
                    v-model.trim="participant.first_name"
                    @change.native="$v.dialog.form.participants.$each[index].first_name.$touch()"
                    :error="$v.dialog.form.participants.$each[index].first_name.$error"
                    type="text"
                    placeholder="First name"
                    name="first_name"
                  />
                  <template #error>
                    <div v-if="$v.dialog.form.participants.$each[index].first_name.$dirty && !$v.dialog.form.participants.$each[index].first_name.required">
                      Required field
                    </div>
                  </template>
                </app-form-group>

                <app-form-group label="Last name" required>
                  <app-input
                    v-model.trim="participant.last_name"
                    @change.native="$v.dialog.form.participants.$each[index].last_name.$touch()"
                    :error="$v.dialog.form.participants.$each[index].last_name.$error"
                    type="text"
                    placeholder="Last name"
                    name="last_name"
                  />
                  <template #error>
                    <div v-if="$v.dialog.form.participants.$each[index].last_name.$dirty && !$v.dialog.form.participants.$each[index].last_name.required">
                      Required field
                    </div>
                  </template>
                </app-form-group>
              </div>

              <div class="massage-dialog__grid">
                <app-form-group label="Phone" required>
                  <app-phone
                    v-model.trim="participant.phone"
                    @change.native="$v.dialog.form.participants.$each[index].phone.$touch()"
                    :error="$v.dialog.form.participants.$each[index].phone.$error"
                    placeholder="+255 786 350 350"
                  />
                  <template #error>
                    <div v-if="$v.dialog.form.participants.$each[index].phone.$dirty && !$v.dialog.form.participants.$each[index].phone.required">
                      Required field
                    </div>
                  </template>
                </app-form-group>

                <app-form-group label="Gender" required>
                  <app-select
                    v-model="participant.gender"
                    :options="gender_options"
                    :reduce="item => item.id"
                    label="name"
                    :clearable="false"
                    placeholder="Select gender"
                    :error="$v.dialog.form.participants.$each[index].gender.$error"
                    @input="$v.dialog.form.participants.$each[index].gender.$touch()"
                  />
                  <template #error>
                    <div v-if="$v.dialog.form.participants.$each[index].gender.$dirty && !$v.dialog.form.participants.$each[index].gender.required">
                      Required field
                    </div>
                  </template>
                </app-form-group>
              </div>
            </div>
          </template>
        </div>

        <div class="massage-dialog__actions">
          <app-button type="button" theme="dark" @click.native="closeDialog">
            Close
          </app-button>
          <app-button
            v-if="dialog.mode === 'edit'"
            type="button"
            theme="error"
            :disabled="dialog.is_submitting"
            @click.native="cancelBooking"
          >
            Cancel Booking
          </app-button>
          <app-button
            ref="submitBooking"
            type="button"
            :disabled="dialog.is_submitting"
            @click.native="saveBooking"
          >
            {{ dialog.mode === 'create' ? 'Create' : 'Save' }}
          </app-button>
        </div>
      </div>
    </modal>

    <modal
      name="massage-slot-block-modal"
      :width="420"
      :height="'auto'"
      :adaptive="true"
      :clickToClose="false"
    >
      <div class="popup massage-dialog">
        <button class="popup__close" type="button" @click="closeBlockDialog"></button>
        <div class="popup__title">Block Massage Slot</div>
        <div class="popup__body">
          <div class="massage-dialog__context">
            <div class="massage-dialog__context-item">
              <span>Date:</span>
              <strong>{{ formatBookingDate(block_dialog.date) }}</strong>
            </div>
            <div class="massage-dialog__context-item">
              <span>Slot:</span>
              <strong>{{ block_dialog.slot_code }}</strong>
            </div>
          </div>
          <app-form-group label="Notes">
            <app-textarea
              v-model.trim="block_dialog.notes"
              placeholder="Reason for blocking"
            />
          </app-form-group>
        </div>
        <div class="popup__actions massage-dialog__actions">
          <app-button type="button" theme="dark" @click.native="closeBlockDialog">Cancel</app-button>
          <app-button
            ref="submitBlock"
            type="button"
            :disabled="is_block_submitting"
            @click.native="saveSlotBlock"
          >
            Block
          </app-button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getHotelManagementPropertiesV2,
  getHotelManagementMassageSchedule,
  getHotelManagementMassageReservations,
  getHotelManagementMassageSlotBlocks,
  getHotelManagementReservationsRoomsMassage,
  getGenders,
  postHotelManagementMassageReservation,
  patchHotelManagementMassageReservation,
  postHotelManagementMassageSlotBlock,
  deleteHotelManagementMassageSlotBlock,
} from '@/http'
import { required } from 'vuelidate/lib/validators'

const MAX_DATE_RANGE_DAYS = 7
const HIDDEN_MASSAGE_SLOT_START_TIME = '22:00'

function formatISODate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function todayISO() {
  return formatISODate(new Date())
}

function parseISODate(value) {
  return new Date(`${value}T00:00:00`)
}

function addDays(value, days) {
  const date = parseISODate(value)
  date.setDate(date.getDate() + days)
  return formatISODate(date)
}

function formatDayLabel(value) {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
  }).format(parseISODate(value))
}

function formatAuditDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const isCurrentYear = date.getFullYear() === new Date().getFullYear()
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: isCurrentYear ? undefined : 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function buildAuditInfo(user, value) {
  const name = user && user.full_name ? user.full_name : ''
  const datetime = formatAuditDateTime(value)
  if (!name && !datetime) return null
  return { name, datetime }
}

function isSameAuditInfo(first, second) {
  if (!first || !second) return false
  return first.name === second.name && first.datetime === second.datetime
}

function formatSlotTime(value) {
  if (!value || typeof value !== 'string') return ''
  return value.slice(0, 5)
}

function buildSlotCode(slot) {
  const from = formatSlotTime(slot.from_time)
  const to = formatSlotTime(slot.to_time)

  if (!from || !to) return ''
  return `${from} - ${to}`
}

function participantTemplate() {
  return {
    id: null,
    guest_person_id: '',
    first_name: '',
    last_name: '',
    phone: '',
    gender: '',
  }
}

export default {
  name: 'HotelManagementMassage',
  validations() {
    return {
      dialog: {
        form: {
          booking_date: { required },
          slot_code: {
            required,
            inSchedule: value => !value || this.slots.some(item => item.code === value),
          },
          source_type: { required },
          reservation_room_id: {
            required: value => this.dialog.form.source_type !== 'existing_guest' || !!value
          },
          participant_count: {
            required: value => this.dialog.form.source_type !== 'walk_in' || !!value,
            allowed: value => this.dialog.form.source_type !== 'walk_in' || [1, 2].includes(value),
            matchesParticipants: () => {
              if (this.dialog.form.source_type !== 'walk_in') return true
              return this.dialog.form.participants.length === this.dialog.form.participant_count
            },
          },
          participants: {
            requiredForSource: value => {
              if (this.dialog.form.source_type !== 'existing_guest') return true
              return Array.isArray(value) && value.length > 0
            },
            slotRuleOk: () => {
              const candidate = this.buildCandidateFromDialog()
              if (!candidate) return true
              return !this.getSlotRuleError(candidate)
            },
            $each: {
              guest_person_id: {
                required: value => {
                  if (this.dialog.form.source_type !== 'existing_guest') return true
                  return !!value
                },
                inSelectedRoom: value => {
                  if (this.dialog.form.source_type !== 'existing_guest' || !value) return true
                  return this.selectedReservationRoomGuests.some(item => item.id === value)
                }
              },
              first_name: {
                required: value => {
                  if (this.dialog.form.source_type !== 'walk_in') return true
                  return !!String(value || '').trim()
                }
              },
              last_name: {
                required: value => {
                  if (this.dialog.form.source_type !== 'walk_in') return true
                  return !!String(value || '').trim()
                }
              },
              phone: {
                required: value => {
                  if (this.dialog.form.source_type !== 'walk_in') return true
                  return value !== '' && value !== null && value !== undefined && value !== 0
                },
                validPhone: value => {
                  if (this.dialog.form.source_type !== 'walk_in') return true
                  return value !== 0
                }
              },
              gender: {
                required: value => {
                  if (this.dialog.form.source_type !== 'walk_in') return true
                  return !!value
                }
              },
            }
          }
        }
      }
    }
  },
  data() {
    const selectedDate = todayISO()
    const selectedEndDate = addDays(selectedDate, 6)
    return {
      selected_property_id: '',
      selected_date: selectedDate,
      selected_date_range: [selectedDate, selectedEndDate],
      search: '',
      properties: [],
      genders: [],
      schedule_slots: [],
      is_schedule_loading: false,
      is_bookings_loading: false,
      is_blocks_loading: false,
      is_schedule_loaded: false,
      schedule_error: '',
      is_initializing_filters: true,
      reservation_rooms: [],
      is_reservation_rooms_loading: false,
      is_dialog_opening: false,
      opening_dialog_type: '',
      opening_dialog_key: null,
      bookings: [],
      blocks: [],
      is_block_submitting: false,
      active_popover: {
        type: '',
        key: '',
      },
      dialog: {
        mode: 'create',
        booking_id: null,
        is_submitting: false,
        form: {
          booking_date: selectedDate,
          slot_code: '',
          source_type: 'existing_guest',
          reservation_room_id: null,
          therapist_position: 1,
          participant_count: 0,
          participants: [],
        },
      },
      block_dialog: {
        date: '',
        slot_code: '',
        notes: '',
      },
    }
  },
  watch: {
    selected_property_id() {
      this.onScheduleFiltersChanged()
    },
    selected_date_range() {
      if (this.normalizeSelectedDateRange()) return

      this.selected_date = this.dateRangeStart
      this.onScheduleFiltersChanged()
    },
  },
  computed: {
    property_options() {
      return this.properties.map(item => ({
        ...item,
        id: item.code,
      }))
    },
    slot_options() {
      return this.slots.map(({ code }) => ({ code }))
    },
    weekDays() {
      const start = this.dateRangeStart
      const end = this.dateRangeEnd
      if (!start || !end) return []

      const dayCount = this.getDaysDiff(start, end) + 1

      return Array.from({ length: dayCount }, (item, index) => {
        const date = addDays(start, index)

        return {
          date,
          label: formatDayLabel(date),
          is_today: date === todayISO(),
        }
      })
    },
    dateRangeStart() {
      const range = Array.isArray(this.selected_date_range) ? this.selected_date_range : []
      const start = range[0]
      const end = range[1]

      if (start && end && start > end) return end
      return start || ''
    },
    dateRangeEnd() {
      const range = Array.isArray(this.selected_date_range) ? this.selected_date_range : []
      const start = range[0]
      const end = range[1]

      if (start && end && start > end) return start
      return end || start || ''
    },
    weekRangeLabel() {
      const firstDay = this.weekDays[0]
      const lastDay = this.weekDays[this.weekDays.length - 1]
      if (!firstDay || !lastDay) return ''
      return `${firstDay.label} - ${lastDay.label}`
    },
    calendarGridStyle() {
      const dayCount = this.weekDays.length || 1
      return {
        gridTemplateColumns: `120px repeat(${dayCount}, minmax(100px, 1fr))`,
      }
    },
    source_type_options() {
      return [
        { id: 'existing_guest', name: 'Hotel Guest' },
        { id: 'walk_in', name: 'Walk-in' },
      ]
    },
    gender_options() {
      if (this.genders.length) {
        return this.genders.map(item => ({
          id: item.code,
          name: item.name,
        }))
      }

      return [{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]
    },
    slots() {
      return this.schedule_slots
    },
    isBoardLoading() {
      return this.is_schedule_loading || this.is_bookings_loading || this.is_blocks_loading
    },
    isDialogOpening() {
      return this.is_dialog_opening
    },
    selectedReservationRoom() {
      return this.reservation_rooms.find(item => item.id === this.dialog.form.reservation_room_id) || null
    },
    selectedReservationRoomGuests() {
      const room = this.selectedReservationRoom
      return room && Array.isArray(room.guests) ? room.guests : []
    },
    selectedExistingGuestIds: {
      get() {
        if (this.dialog.form.source_type !== 'existing_guest') return []
        return this.dialog.form.participants
          .map(item => item.guest_person_id)
          .filter(Boolean)
      },
      set(value) {
        const ids = Array.isArray(value) ? value.filter(Boolean) : []
        const prevById = this.dialog.form.participants.reduce((acc, item) => {
          if (item && item.guest_person_id) acc[item.guest_person_id] = item
          return acc
        }, {})

        this.dialog.form.participants = ids.map(id => ({
          ...participantTemplate(),
          ...(prevById[id] || {}),
          guest_person_id: id,
        }))
      }
    },
    currentBookings() {
      return this.bookings.filter(item => {
        return item.status === 'active' &&
          item.property_id === this.selected_property_id
      })
    },
    is_therapist() {
      return this.$store.state.userGroups.map(item => item.code).includes('therapist')
    },
    is_hotel_reception() {
      return this.$store.state.userGroups.map(item => item.code).includes('hotel_reception')
    },
    can_manage_slot_blocks() {
      const roleCodes = this.$store.state.userGroups.map(item => item.code)
      return roleCodes.includes('hotel_reception') || roleCodes.includes('general_manager')
    },
  },
  async created() {
    await this.loadGenderOptions()
    await this.initPropertyAndSchedule()
  },
  mounted() {
    document.addEventListener('click', this.closePopover)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closePopover)
  },
  methods: {
    getDaysDiff(start, end) {
      const startTime = parseISODate(start).getTime()
      const endTime = parseISODate(end).getTime()
      const dayMs = 24 * 60 * 60 * 1000
      return Math.max(Math.round((endTime - startTime) / dayMs), 0)
    },
    getDateRangeLength() {
      if (!this.dateRangeStart || !this.dateRangeEnd) return MAX_DATE_RANGE_DAYS
      return Math.min(this.getDaysDiff(this.dateRangeStart, this.dateRangeEnd) + 1, MAX_DATE_RANGE_DAYS)
    },
    setDateRange(start, dayCount) {
      const normalizedDayCount = Math.min(dayCount, MAX_DATE_RANGE_DAYS)
      this.selected_date_range = [start, addDays(start, normalizedDayCount - 1)]
    },
    normalizeSelectedDateRange() {
      if (!this.dateRangeStart || !this.dateRangeEnd) return false
      if (this.getDaysDiff(this.dateRangeStart, this.dateRangeEnd) < MAX_DATE_RANGE_DAYS) return false

      this.selected_date_range = [this.dateRangeStart, addDays(this.dateRangeStart, MAX_DATE_RANGE_DAYS - 1)]
      return true
    },
    shiftDateRange(days) {
      if (!this.dateRangeStart || !this.dateRangeEnd) return

      this.selected_date_range = [
        addDays(this.dateRangeStart, days),
        addDays(this.dateRangeEnd, days),
      ]
    },
    resetDialogValidation() {
      this.$nextTick(() => {
        if (this.$v && this.$v.dialog && this.$v.dialog.form) this.$v.dialog.form.$reset()
      })
    },
    getBookingPopoverKey(booking) {
      return booking ? `booking-${booking.id}` : ''
    },
    getEmptyPopoverKey(date, slotCode, emptyIndex) {
      return `empty-${date}-${slotCode}-${emptyIndex}`
    },
    getBlockPopoverKey(block) {
      return block ? `block-${block.id}` : ''
    },
    closePopover() {
      this.active_popover = {
        type: '',
        key: '',
      }
    },
    togglePopover(type, key) {
      if (this.active_popover.type === type && this.active_popover.key === key) {
        this.closePopover()
        return
      }

      this.active_popover = { type, key }
    },
    toggleBookingPopover(booking) {
      if (this.isDialogOpening) return
      this.togglePopover('booking', this.getBookingPopoverKey(booking))
    },
    toggleEmptyPopover(date, slotCode, emptyIndex) {
      if (this.isDialogOpening) return
      this.togglePopover('empty', this.getEmptyPopoverKey(date, slotCode, emptyIndex))
    },
    toggleBlockPopover(block) {
      this.togglePopover('block', this.getBlockPopoverKey(block))
    },
    isBookingPopoverActive(booking) {
      return this.active_popover.type === 'booking' && this.active_popover.key === this.getBookingPopoverKey(booking)
    },
    isEmptyPopoverActive(date, slotCode, emptyIndex) {
      return this.active_popover.type === 'empty' && this.active_popover.key === this.getEmptyPopoverKey(date, slotCode, emptyIndex)
    },
    isBlockPopoverActive(block) {
      return this.active_popover.type === 'block' && this.active_popover.key === this.getBlockPopoverKey(block)
    },
    getUserHotelPropertyCode() {
      const userData = this.$store && this.$store.state ? this.$store.state.userData : null
      const hp = userData ? userData.hotel_property : null

      if (!hp) return ''
      return typeof hp === 'string' ? hp : (hp.code || '')
    },
    async initPropertyAndSchedule() {
      this.is_initializing_filters = true
      try {
        await this.loadProperties()
      } finally {
        this.is_initializing_filters = false
      }
      await this.onScheduleFiltersChanged()
    },
    async loadProperties() {
      try {
        const { data } = await getHotelManagementPropertiesV2('')
        const list = Array.isArray(data) ? data : []

        this.properties = list
        if (!list.length) {
          this.selected_property_id = ''
          return
        }

        const userPropertyCode = this.getUserHotelPropertyCode()
        if (userPropertyCode) {
          const inList = list.find(item => item.code === userPropertyCode)
          if (inList) {
            this.selected_property_id = inList.code
            return
          }
        }

        if (!this.selected_property_id || !list.some(item => item.code === this.selected_property_id)) {
          this.selected_property_id = list[0].code
        }
      } catch (e) {
        this.properties = []
        this.selected_property_id = ''
      }
    },
    async loadGenderOptions() {
      try {
        const { data } = await getGenders()
        this.genders = Array.isArray(data) ? data : []
      } catch (e) {
        this.genders = []
      }
    },
    onScheduleFiltersChanged() {
      if (this.is_initializing_filters) return
      this.closePopover()
      if (!this.selected_property_id || !this.dateRangeStart || !this.dateRangeEnd) {
        this.schedule_slots = []
        this.bookings = []
        this.blocks = []
        this.is_schedule_loaded = true
        return
      }
      return Promise.all([
        this.loadSchedule(),
        this.loadBookings(),
        this.loadBlocks(),
      ])
    },
    normalizeScheduleSlots(scheduleList) {
      const rawSlots = []
      const scheduleItems = Array.isArray(scheduleList) ? scheduleList : []

      scheduleItems.forEach(item => {
        if (item && Array.isArray(item.slots)) rawSlots.push(...item.slots)
      })

      return rawSlots
        .map(slot => {
          const capacity = Number(slot.capacity)

          return {
            id: slot.id,
            code: buildSlotCode(slot),
            from_label: formatSlotTime(slot.from_time),
            from_time: slot.from_time,
            to_time: slot.to_time,
            capacity: Number.isFinite(capacity) ? Math.min(2, Math.max(0, capacity)) : 0,
          }
        })
        .filter(slot => (
          slot.code && slot.from_label !== HIDDEN_MASSAGE_SLOT_START_TIME
        ))
    },
    async loadSchedule() {
      this.is_schedule_loading = true
      this.schedule_error = ''
      try {
        const { data } = await getHotelManagementMassageSchedule({
          property: this.selected_property_id,
          date: this.dateRangeStart,
        })
        this.schedule_slots = this.normalizeScheduleSlots(data)
      } catch (e) {
        this.schedule_slots = []
        this.schedule_error = 'Failed to load schedule'
      } finally {
        this.is_schedule_loading = false
        this.is_schedule_loaded = true

        if (this.dialog.mode === 'create' && !this.dialog.booking_id) {
          const firstSlot = this.slots[0]
          this.dialog.form.slot_code = firstSlot ? firstSlot.code : ''
        }
      }
    },
    normalizeMassageReservations(list) {
      return (Array.isArray(list) ? list : []).map(item => {
        const roomGuests = Array.isArray(item.room_guests) ? item.room_guests : []
        const walkInGuests = Array.isArray(item.walk_in_guests) ? item.walk_in_guests : []

        const roomGroups = roomGuests.map(roomGuestItem => {
          const reservationRoom = roomGuestItem && roomGuestItem.reservation_room ? roomGuestItem.reservation_room : null
          const reservationRoomId = reservationRoom ? reservationRoom.id : null
          const roomShortName = reservationRoom && reservationRoom.room ? (reservationRoom.room.short_name || '') : ''
          const guests = Array.isArray(roomGuestItem && roomGuestItem.guests) ? roomGuestItem.guests : []
          const normalizedGuests = guests.map(guest => ({
            source_type: 'existing_guest',
            guest_person_id: guest.id,
            reservation_room_id: reservationRoomId,
            room_short_name: roomShortName,
            full_name: guest.full_name || '',
            phone: '',
            gender: guest.gender && guest.gender.code ? guest.gender.code : '',
          }))

          return {
            id: roomGuestItem ? roomGuestItem.id : null,
            reservation_room_id: reservationRoomId,
            room_short_name: roomShortName,
            guests: normalizedGuests,
            is_couple: normalizedGuests.length === 2 && (Boolean(roomGuestItem && roomGuestItem.is_couple) || this.hasCoupleGenders(normalizedGuests)),
          }
        })

        const existingParticipants = roomGuests.flatMap(roomGuestItem => {
          const reservationRoomId = roomGuestItem && roomGuestItem.reservation_room ? roomGuestItem.reservation_room.id : null
          let roomShortName = ''
          if (roomGuestItem && roomGuestItem.reservation_room && roomGuestItem.reservation_room.room) {
            roomShortName = roomGuestItem.reservation_room.room.short_name || ''
          }
          const guests = Array.isArray(roomGuestItem && roomGuestItem.guests) ? roomGuestItem.guests : []

          return guests.map(guest => ({
            source_type: 'existing_guest',
            guest_person_id: guest.id,
            reservation_room_id: reservationRoomId,
            room_short_name: roomShortName,
            full_name: guest.full_name || '',
            phone: '',
            gender: guest.gender && guest.gender.code ? guest.gender.code : '',
          }))
        })

        const walkInParticipants = walkInGuests.map(guest => {
          let gender = ''
          if (guest.gender && typeof guest.gender === 'object') gender = guest.gender.code || ''
          else gender = guest.gender || ''

          return {
            id: guest.id || null,
            source_type: 'walk_in',
            guest_person_id: null,
            first_name: guest.first_name || '',
            last_name: guest.last_name || '',
            full_name: `${guest.first_name || ''} ${guest.last_name || ''}`.trim(),
            phone: guest.phone || '',
            gender,
          }
        })

        const participants = existingParticipants.concat(walkInParticipants)
        const slotCode = item.slot ? buildSlotCode(item.slot) : ''
        const sourceType = existingParticipants.length ? 'existing_guest' : 'walk_in'
        const firstRoomGuest = roomGuests[0]
        const reservationRoomId = firstRoomGuest && firstRoomGuest.reservation_room ? firstRoomGuest.reservation_room.id : null
        const isCouple = roomGroups.some(group => group.is_couple)
        const createdAudit = buildAuditInfo(item.created_by, item.created_at)
        const updatedAudit = buildAuditInfo(item.updated_by, item.updated_at)

        return {
          id: item.id,
          property_id: item.property && item.property.code ? item.property.code : '',
          booking_date: item.date || '',
          slot_code: slotCode,
          therapist_position: item.therapist_position || 1,
          source_type: sourceType,
          reservation_room_id: reservationRoomId,
          is_couple: isCouple,
          room_groups: roomGroups,
          walk_in_guests: walkInParticipants,
          participants,
          status: item.status && item.status.code === 'cancelled' ? 'cancelled' : 'active',
          created_audit: createdAudit,
          updated_audit: isSameAuditInfo(updatedAudit, createdAudit) ? null : updatedAudit,
        }
      })
    },
    async loadBookings() {
      this.is_bookings_loading = true
      try {
        const { data } = await getHotelManagementMassageReservations({
          property: this.selected_property_id,
          from_date: this.dateRangeStart,
          to_date: this.dateRangeEnd,
        })
        this.bookings = this.normalizeMassageReservations(data)
      } catch (e) {
        this.bookings = []
      } finally {
        this.is_bookings_loading = false
      }
    },
    normalizeMassageSlotBlocks(list) {
      return (Array.isArray(list) ? list : [])
        .map(item => ({
          id: item.id,
          property_id: item.property && item.property.code ? item.property.code : '',
          date: item.date || '',
          slot_code: item.slot ? buildSlotCode(item.slot) : '',
          notes: item.notes || '',
          created_by_name: item.created_by && item.created_by.full_name ? item.created_by.full_name : '',
        }))
        .filter(item => item.id && item.date && item.slot_code)
    },
    async loadBlocks() {
      this.is_blocks_loading = true
      try {
        const { data } = await getHotelManagementMassageSlotBlocks({
          property: this.selected_property_id,
          date_from: this.dateRangeStart,
          date_to: this.dateRangeEnd,
        })
        this.blocks = this.normalizeMassageSlotBlocks(data)
      } catch (e) {
        this.blocks = []
      } finally {
        this.is_blocks_loading = false
      }
    },
    getSlotCapacity(slotCode) {
      const slot = this.slots.find(item => item.code === slotCode)
      return slot ? slot.capacity : 0
    },
    getSlotByCode(slotCode) {
      return this.slots.find(item => item.code === slotCode) || null
    },
    roomOptionLabel(item) {
      if (!item) return ''
      const groupName = item.group_name || (item.reservation && item.reservation.group_name ? item.reservation.group_name : '')
      const shortName = item.room && item.room.short_name ? item.room.short_name : ''
      return `${groupName} (${shortName})`
    },
    guestOptionLabelByRoom(guest) {
      if (!guest) return ''
      const genderName = guest.gender && guest.gender.name ? guest.gender.name : ''
      return genderName ? `${guest.full_name} (${genderName})` : guest.full_name
    },
    async loadReservationRoomsForDialog() {
      if (this.dialog.form.source_type !== 'existing_guest') {
        this.reservation_rooms = []
        this.dialog.form.reservation_room_id = null
        return
      }
      if (!this.selected_property_id || !this.dialog.form.booking_date) {
        this.reservation_rooms = []
        this.dialog.form.reservation_room_id = null
        return
      }

      this.is_reservation_rooms_loading = true
      try {
        const { data } = await getHotelManagementReservationsRoomsMassage({
          property: this.selected_property_id,
          date: this.dialog.form.booking_date,
        })
        this.reservation_rooms = (Array.isArray(data) ? data : [])
          .filter(item => Array.isArray(item.guests) && item.guests.length > 0)
          .map(item => ({
            ...item,
            room_option_label: this.roomOptionLabel(item),
          }))

        const hasSelected = this.reservation_rooms.some(item => item.id === this.dialog.form.reservation_room_id)
        if (!hasSelected) this.dialog.form.reservation_room_id = null
      } catch (e) {
        this.reservation_rooms = []
        this.dialog.form.reservation_room_id = null
      } finally {
        this.is_reservation_rooms_loading = false
      }
    },
    onDialogSourceTypeChange() {
      this.dialog.form.reservation_room_id = null
      if (this.dialog.form.source_type === 'existing_guest') {
        this.dialog.form.participants = []
      } else {
        this.dialog.form.participants = [participantTemplate()]
        this.dialog.form.participant_count = 1
      }
      this.resetDialogValidation()
      this.loadReservationRoomsForDialog()
    },
    onDialogReservationRoomChange() {
      if (this.dialog.form.source_type === 'existing_guest') {
        this.dialog.form.participants = []
      } else {
        this.dialog.form.participants = this.dialog.form.participants.map(item => ({
          ...item,
          guest_person_id: '',
        }))
      }
      this.resetDialogValidation()
    },
    async onDialogBookingDateChange(value) {
      if (this.$v && this.$v.dialog && this.$v.dialog.form && this.$v.dialog.form.booking_date) {
        this.$v.dialog.form.booking_date.$touch()
      }
      if (this.dialog.form.source_type === 'existing_guest') await this.loadReservationRoomsForDialog()
      return value
    },
    getGuestById(guestId) {
      return this.selectedReservationRoomGuests.find(item => item.id === guestId) || null
    },
    canSelectExistingGuest(guest) {
      if (!guest || !guest.id) return false

      const selectedIds = this.selectedExistingGuestIds
      if (selectedIds.includes(guest.id)) return true

      const slotCode = this.dialog.form.slot_code
      if (!slotCode) return false

      const available = this.getSlotAvailable(
        this.dialog.form.booking_date,
        slotCode,
        this.dialog.mode === 'edit' ? this.dialog.booking_id : null
      )
      const booking = this.getDialogBooking()
      const retainedWalkIns = booking && booking.walk_in_guests ? booking.walk_in_guests.length : 0
      return selectedIds.length < Math.max(available - retainedWalkIns, 0)
    },
    isCreateButtonLoading(date, slotCode) {
      return this.is_dialog_opening && this.opening_dialog_type === 'create' && this.opening_dialog_key === `${date}-${slotCode}`
    },
    isEditButtonLoading(bookingId) {
      return this.is_dialog_opening && this.opening_dialog_type === 'edit' && this.opening_dialog_key === bookingId
    },
    normalizeWalkInGuestPayload(guest) {
      const payload = {
        first_name: (guest.first_name || '').trim(),
        last_name: (guest.last_name || '').trim(),
        phone: (guest.phone || '').trim(),
        gender: guest.gender || '',
      }

      if (guest.id) payload.id = guest.id
      return payload
    },
    getDialogBooking() {
      if (this.dialog.mode !== 'edit' || !this.dialog.booking_id) return null
      return this.bookings.find(item => item.id === this.dialog.booking_id) || null
    },
    getGuestReservationRoomId(guest) {
      if (!guest) return null

      if (this.dialog.form && this.dialog.form.reservation_room_id) return this.dialog.form.reservation_room_id

      if (typeof guest.reservation_room === 'number') return guest.reservation_room
      if (guest.reservation_room && typeof guest.reservation_room.id === 'number') return guest.reservation_room.id
      if (typeof guest.reservation_room_id === 'number') return guest.reservation_room_id

      return null
    },
    buildMassageReservationPayload() {
      const slot = this.getSlotByCode(this.dialog.form.slot_code)
      if (!slot) return null

      const roomGuestsMap = {}
      const walkInGuests = []

      this.dialog.form.participants.forEach(rawParticipant => {
        if (this.dialog.form.source_type === 'existing_guest') {
          const guest = this.getGuestById(rawParticipant.guest_person_id)
          if (!guest) return

          const reservationRoomId = this.getGuestReservationRoomId(guest)
          if (!reservationRoomId) return

          if (!roomGuestsMap[reservationRoomId]) roomGuestsMap[reservationRoomId] = []
          roomGuestsMap[reservationRoomId].push(guest.id)
          return
        }

        walkInGuests.push(this.normalizeWalkInGuestPayload(rawParticipant))
      })

      const dialogBooking = this.getDialogBooking()
      if (
        this.dialog.mode === 'edit' &&
        this.dialog.form.source_type === 'existing_guest' &&
        dialogBooking &&
        Array.isArray(dialogBooking.walk_in_guests) &&
        dialogBooking.walk_in_guests.length
      ) {
        dialogBooking.walk_in_guests.forEach(guest => {
          walkInGuests.push(this.normalizeWalkInGuestPayload(guest))
        })
      }

      return {
        property: this.selected_property_id,
        date: this.dialog.form.booking_date,
        slot: slot.id,
        therapist_position: this.dialog.form.therapist_position,
        room_guests: Object.entries(roomGuestsMap).map(([reservationRoomId, guests]) => ({
          reservation_room: Number(reservationRoomId),
          guests,
        })),
        walk_in_guests: walkInGuests,
      }
    },
    buildCandidateFromDialog() {
      const participants = this.dialog.form.participants
        .map(item => this.resolveParticipant(item))
        .filter(Boolean)

      if (this.dialog.form.source_type === 'walk_in' && participants.length !== this.dialog.form.participant_count) return null
      if (this.dialog.form.source_type === 'existing_guest' && !participants.length) return null

      const booking = this.getDialogBooking()
      if (this.dialog.form.source_type === 'existing_guest' && booking) {
        participants.push(...(booking.walk_in_guests || []))
      }

      return {
        id: this.dialog.mode === 'create' ? null : this.dialog.booking_id,
        property_id: this.selected_property_id,
        booking_date: this.dialog.form.booking_date,
        slot_code: this.dialog.form.slot_code,
        therapist_position: this.dialog.form.therapist_position,
        source_type: this.dialog.form.source_type,
        reservation_room_id: this.dialog.form.reservation_room_id,
        is_couple: false,
        participant_count: participants.length,
        participants,
        status: 'active',
      }
    },
    formatBookingDate(dateString) {
      if (!dateString) return ''
      return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }).format(new Date(`${dateString}T00:00:00`))
    },
    getWalkInGuestName(guest) {
      if (!guest) return ''
      return `${guest.first_name || ''} ${guest.last_name || ''}`.trim() || guest.full_name || ''
    },
    guestOptionLabel(guest) {
      return `${guest.full_name} (${guest.phone})`
    },
    getParticipantIconSrc(participant) {
      if (!participant) return ''
      if (participant.gender === 'male') return require('@/assets/img/male-icon.svg')
      if (participant.gender === 'female') return require('@/assets/img/female-icon.svg')
      return ''
    },
    getParticipantInfoLabel(participant) {
      if (!participant || !participant.gender) return 'Not specified'
      if (participant.gender === 'male') return 'Male'
      if (participant.gender === 'female') return 'Female'
      return 'Not specified'
    },
    hasCoupleGenders(participants) {
      const genders = (Array.isArray(participants) ? participants : []).map(item => item.gender)
      return genders.includes('male') && genders.includes('female')
    },
    getBookingCalendarGroups(booking) {
      if (!booking) return []

      const groups = []
      const roomGroups = Array.isArray(booking.room_groups) ? booking.room_groups : []

      roomGroups.forEach((group, index) => {
        const participants = Array.isArray(group.guests) ? group.guests : []
        if (!participants.length) return

        groups.push({
          key: `room-${booking.id}-${group.id || group.reservation_room_id || index}`,
          participants,
          is_couple: Boolean(group.is_couple),
        })
      })

      const walkInGuests = Array.isArray(booking.walk_in_guests) ? booking.walk_in_guests : []
      walkInGuests.forEach((guest, index) => {
        groups.push({
          key: `walk-in-${booking.id}-${guest.id || index}`,
          participants: [guest],
          is_couple: false,
        })
      })

      return groups
    },
    matchesSearch(booking) {
      if (!this.search) return true
      const searchNormalized = this.search.toLowerCase()
      return booking.participants.some(participant => {
        const name = participant.full_name ? participant.full_name.toLowerCase() : ''
        const phone = participant.phone ? participant.phone.toLowerCase() : ''
        return name.includes(searchNormalized) || phone.includes(searchNormalized)
      })
    },
    getSlotBookings(date, slotCode) {
      return this.currentBookings.filter(item => item.booking_date === date && item.slot_code === slotCode)
    },
    getSlotBlock(date, slotCode) {
      return this.blocks.find(item => {
        return item.property_id === this.selected_property_id && item.date === date && item.slot_code === slotCode
      }) || null
    },
    getVisibleSlotBookings(date, slotCode) {
      return this.getSlotBookings(date, slotCode).filter(item => this.matchesSearch(item))
    },
    getSlotOccupied(date, slotCode, withoutBookingId = null) {
      return this.getSlotBookings(date, slotCode)
        .filter(item => item.id !== withoutBookingId)
        .reduce((acc, item) => acc + item.participants.length, 0)
    },
    getSlotAvailable(date, slotCode, withoutBookingId = null) {
      return Math.max(this.getSlotCapacity(slotCode) - this.getSlotOccupied(date, slotCode, withoutBookingId), 0)
    },
    getTherapistPositions(booking) {
      return booking.participants.length >= 2 ? [1, 2] : [booking.therapist_position || 1]
    },
    getAvailableTherapistPositions(date, slotCode) {
      const occupied = this.getSlotBookings(date, slotCode).flatMap(this.getTherapistPositions)
      return [1, 2].filter(position => !occupied.includes(position))
    },
    getDialogCapacityError() {
      const candidate = this.buildCandidateFromDialog()
      if (!candidate) return ''
      const messages = {
        slot_capacity_exceeded: 'A time slot has two places, one per therapist.',
        therapist_occupied: 'This therapist is already booked for the selected time.',
        slot_blocked: 'This time slot is blocked.',
        slot_not_in_schedule: 'Select an available time slot.',
      }
      return messages[this.getSlotRuleError(candidate)] || ''
    },
    getParticipantGenderClass(participant) {
      return {
        'massage-calendar__guest--male': participant.gender === 'male',
        'massage-calendar__guest--female': participant.gender === 'female',
        'massage-calendar__guest--free': !participant.gender,
      }
    },
    goToPrevWeek() {
      this.shiftDateRange(-this.getDateRangeLength())
    },
    goToNextWeek() {
      this.shiftDateRange(this.getDateRangeLength())
    },
    goToToday() {
      this.setDateRange(todayISO(), this.getDateRangeLength())
    },
    participantLabel(count) {
      return count === 1 ? '1 participant' : `${count} participants`
    },
    openCreateFromPopover(date, slotCode, therapistPosition) {
      if (this.is_therapist) return
      this.closePopover()
      this.openCreateDialog(date, slotCode, therapistPosition)
    },
    openBlockDialog(date, slotCode) {
      if (!this.can_manage_slot_blocks || this.getSlotBookings(date, slotCode).length) return
      this.closePopover()
      this.block_dialog = {
        date,
        slot_code: slotCode,
        notes: '',
      }
      this.$modal.show('massage-slot-block-modal')
    },
    closeBlockDialog() {
      this.$modal.hide('massage-slot-block-modal')
    },
    async saveSlotBlock() {
      if (!this.can_manage_slot_blocks || this.is_block_submitting) return
      const slot = this.getSlotByCode(this.block_dialog.slot_code)
      if (!slot) return

      this.is_block_submitting = true
      if (this.$refs.submitBlock) this.$refs.submitBlock.preload = true
      try {
        await postHotelManagementMassageSlotBlock({
          property: this.selected_property_id,
          date: this.block_dialog.date,
          slot: slot.id,
          notes: this.block_dialog.notes,
        })
        await this.loadBlocks()
        this.closeBlockDialog()
      } finally {
        if (this.$refs.submitBlock) this.$refs.submitBlock.preload = false
        this.is_block_submitting = false
      }
    },
    async deleteSlotBlock(block) {
      if (!this.can_manage_slot_blocks || !block || !block.id || this.is_block_submitting) return

      this.is_block_submitting = true
      try {
        await deleteHotelManagementMassageSlotBlock(block.id)
        this.closePopover()
        await this.loadBlocks()
      } finally {
        this.is_block_submitting = false
      }
    },
    openEditFromPopover(booking) {
      if (this.is_therapist) return
      this.closePopover()
      this.openEditDialog(booking)
    },
    async openCreateDialog(date, slotCode, therapistPosition = 1) {
      if (this.is_therapist) return
      if (this.is_dialog_opening) return

      this.is_dialog_opening = true
      this.opening_dialog_type = 'create'
      this.opening_dialog_key = `${date}-${slotCode}`
      this.dialog.mode = 'create'
      this.dialog.booking_id = null
      this.dialog.is_submitting = false
      this.reservation_rooms = []
      this.dialog.form = {
        booking_date: date,
        slot_code: slotCode,
        therapist_position: therapistPosition,
        source_type: 'existing_guest',
        reservation_room_id: null,
        participant_count: 0,
        participants: [],
      }
      this.resetDialogValidation()
      try {
        await this.loadReservationRoomsForDialog()
        this.$modal.show('massage-booking-modal')
      } finally {
        this.is_dialog_opening = false
        this.opening_dialog_type = ''
        this.opening_dialog_key = null
      }
    },
    async openEditDialog(booking) {
      if (this.is_therapist) return
      if (this.is_dialog_opening) return

      this.is_dialog_opening = true
      this.opening_dialog_type = 'edit'
      this.opening_dialog_key = booking.id
      this.dialog.mode = 'edit'
      this.dialog.booking_id = booking.id
      this.dialog.is_submitting = false
      this.reservation_rooms = []
      this.dialog.form = {
        booking_date: booking.booking_date,
        slot_code: booking.slot_code,
        therapist_position: booking.therapist_position || 1,
        source_type: booking.source_type || 'walk_in',
        reservation_room_id: booking.reservation_room_id || null,
        participant_count: booking.source_type === 'walk_in' ? booking.participants.length : 0,
        participants: booking.participants.map(participant => ({
          id: participant.id || null,
          guest_person_id: participant.guest_person_id || '',
          first_name: participant.source_type === 'walk_in' ? (participant.first_name || '') : '',
          last_name: participant.source_type === 'walk_in' ? (participant.last_name || '') : '',
          phone: participant.phone || '',
          gender: (participant.gender && participant.gender.code) || participant.gender || '',
        })),
      }
      this.resetDialogValidation()
      try {
        if (this.dialog.form.source_type === 'existing_guest') await this.loadReservationRoomsForDialog()
        this.$modal.show('massage-booking-modal')
      } finally {
        this.is_dialog_opening = false
        this.opening_dialog_type = ''
        this.opening_dialog_key = null
      }
    },
    closeDialog() {
      this.resetDialogValidation()
      this.$modal.hide('massage-booking-modal')
    },
    setParticipantCount(count) {
      this.dialog.form.participant_count = count
      this.syncParticipantsCount()
    },
    syncParticipantsCount() {
      const count = this.dialog.form.participant_count
      if (this.dialog.form.participants.length > count) {
        this.dialog.form.participants = this.dialog.form.participants.slice(0, count)
      } else {
        while (this.dialog.form.participants.length < count) {
          this.dialog.form.participants.push(participantTemplate())
        }
      }
      this.resetDialogValidation()
    },
    resolveParticipant(rawParticipant) {
      if (this.dialog.form.source_type === 'existing_guest') {
        const guest = this.selectedReservationRoomGuests.find(item => item.id === rawParticipant.guest_person_id)
        if (!guest) return null
        return {
          source_type: 'existing_guest',
          guest_person_id: guest.id,
          full_name: guest.full_name,
          phone: guest.phone || '',
          gender: guest.gender && guest.gender.code ? guest.gender.code : '',
        }
      }
      return {
        id: rawParticipant.id || null,
        source_type: 'walk_in',
        guest_person_id: null,
        first_name: rawParticipant.first_name,
        last_name: rawParticipant.last_name,
        full_name: `${rawParticipant.first_name} ${rawParticipant.last_name}`.trim(),
        phone: rawParticipant.phone.trim(),
        gender: rawParticipant.gender,
      }
    },
    getSlotRuleError(candidate) {
      const otherBookings = this.bookings.filter(item => {
        return item.status === 'active' &&
          item.property_id === candidate.property_id &&
          item.booking_date === candidate.booking_date &&
          item.id !== candidate.id
      })
      const slotBookings = otherBookings.filter(item => item.slot_code === candidate.slot_code)
      const occupied = slotBookings.reduce((acc, item) => acc + item.participants.length, 0)
      const slotCapacity = this.getSlotCapacity(candidate.slot_code)
      if (!slotCapacity) return 'slot_not_in_schedule'
      if (this.getSlotBlock(candidate.booking_date, candidate.slot_code)) return 'slot_blocked'
      if (occupied + candidate.participants.length > slotCapacity) return 'slot_capacity_exceeded'
      const lanes = this.getTherapistPositions(candidate)
      if (slotBookings.some(item => this.getTherapistPositions(item).some(lane => lanes.includes(lane)))) return 'therapist_occupied'
      return ''
    },
    async saveBooking() {
      if (this.is_therapist) return
      if (this.dialog.is_submitting) return
      this.$v.dialog.form.$touch()
      if (this.$v.dialog.form.$invalid) return

      const candidate = this.buildCandidateFromDialog()
      if (!candidate) return

      const payload = this.buildMassageReservationPayload()
      if (!payload) return

      this.dialog.is_submitting = true
      if (this.$refs.submitBooking) this.$refs.submitBooking.preload = true
      try {
        if (this.dialog.mode === 'create') {
          await postHotelManagementMassageReservation(payload)
          await this.loadBookings()
        } else {
          await patchHotelManagementMassageReservation(this.dialog.booking_id, payload)
          await this.loadBookings()
        }
      } finally {
        if (this.$refs.submitBooking) this.$refs.submitBooking.preload = false
        this.dialog.is_submitting = false
      }
      this.closeDialog()
    },
    async cancelBooking() {
      if (this.is_therapist) return
      if (this.dialog.is_submitting) return
      const index = this.bookings.findIndex(item => item.id === this.dialog.booking_id)
      if (index === -1) return

      this.dialog.is_submitting = true
      try {
        await patchHotelManagementMassageReservation(this.dialog.booking_id, { status: 'cancelled' })
        await this.loadBookings()
      } finally {
        this.dialog.is_submitting = false
      }
      this.closeDialog()
    },
  }
}
</script>

<style scoped>
.massage-page {
  max-width: 1160px;
}

.massage-page__filters {
  display: grid;
  grid-template-columns: 260px 260px 1fr;
  gap: 12px;
}

.massage-board {
  position: relative;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: visible;
  background: #fff;
}

.massage-board__overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.68);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  cursor: wait;
}

.massage-board__overlay-spinner {
  width: 28px;
  height: 28px;
}

.massage-board__placeholder {
  padding: 18px 14px;
  border-top: 1px solid #f0f0f0;
  color: #8a8a8a;
  font-size: 14px;
}

.massage-board__nav {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;
  padding: 14px 18px;
}

.massage-board__nav-side {
  display: flex;
  gap: 8px;
}

.massage-board__nav-side--right {
  justify-content: flex-end;
}

.massage-board__nav-btn {
  min-width: 90px;
  height: 42px;
  padding: 0 18px;
  border: 1px solid #dedede;
  border-radius: 8px;
  background: #fff;
  color: #252525;
  font-size: 18px;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  cursor: pointer;
}

.massage-board__range {
  color: #161616;
  font-size: 20px;
  font-weight: 700;
  text-align: center;
}

.massage-calendar {
  min-width: 900px;
  border-top: 1px solid #e9e9e2;
}

.massage-calendar__head,
.massage-calendar__row {
  display: grid;
}

.massage-calendar__time-head,
.massage-calendar__day-head,
.massage-calendar__time,
.massage-calendar__cell {
  border-right: 1px solid #e3e3dd;
  border-bottom: 1px solid #e3e3dd;
}

.massage-calendar__time-head,
.massage-calendar__day-head {
  min-height: 40px;
  padding: 10px 8px;
  background: #f5f4ed;
  color: #252525;
  font-size: 16px;
  font-weight: 700;
}

.massage-calendar__day-head {
  text-align: center;
}

.massage-calendar__day-head--today,
.massage-calendar__cell--today {
  background: #f3f3f8;
}

.massage-calendar__time {
  padding: 16px 12px 8px;
  background: #faf9f2;
  color: #2c2c2c;
  font-size: 18px;
  font-weight: 700;
}

.massage-calendar__cell {
  position: relative;
  min-height: 53px;
  padding: 10px 8px;
}

.massage-calendar__cell-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 4px;
  min-height: 28px;
}

.massage-calendar__booking,
.massage-calendar__empty,
.massage-calendar__blocked {
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 50%;
  background: #fff;
  line-height: 0;
  cursor: pointer;
}

.massage-calendar__slot-wrap {
  position: relative;
  display: inline-flex;
  max-width: 100%;
}

.massage-calendar__booking {
  display: flex;
  flex-wrap: wrap;
  height: auto;
  min-height: 28px;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: auto;
  min-width: 28px;
  border: 0;
  background: transparent;
}

.massage-calendar__booking-group {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
}

.massage-calendar__booking-group--couple {
  padding: 2px;
  border: 2px solid #7b6ee6;
  border-radius: 18px;
  background: #eeeafc;
}

.massage-calendar__empty {
  border: 2px dashed #d3d3d3;
}

.massage-calendar__blocked {
  border: 0;
  background: #4b4b4b;
  cursor: pointer;
}

.massage-calendar__cell--today .massage-calendar__empty {
  background: #f3f3f8;
}

.massage-calendar__empty span,
.massage-calendar__blocked span {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.massage-calendar__guest {
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #3e96d8;
}

.massage-calendar__guest--female {
  background: #d84e7c;
}

.massage-calendar__guest--free {
  background: #bcbcbc;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
}

.massage-calendar__guest-icon {
  width: 15px;
  height: 15px;
  filter: brightness(0) invert(1);
}

.massage-calendar__guest-text {
  color: #fff;
  font-size: 15px;
  line-height: 1;
  font-weight: 700;
}

.massage-calendar__spinner {
  width: 16px;
  height: 16px;
}

.massage-popover {
  position: absolute;
  left: 50%;
  top: calc(100% + 8px);
  z-index: 5;
  width: 230px;
  padding: 10px;
  border: 1px solid #deded7;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  color: #252525;
  line-height: 1.25;
  text-align: left;
  transform: translateX(-50%);
}

.massage-popover:before {
  content: '';
  position: absolute;
  left: 50%;
  top: -6px;
  width: 10px;
  height: 10px;
  border-top: 1px solid #deded7;
  border-left: 1px solid #deded7;
  background: #fff;
  transform: translateX(-50%) rotate(45deg);
}

.massage-calendar__row:nth-last-child(-n+2) .massage-popover {
  top: auto;
  bottom: calc(100% + 8px);
}

.massage-calendar__row:nth-last-child(-n+2) .massage-popover:before {
  top: auto;
  bottom: -6px;
  border-top: 0;
  border-right: 1px solid #deded7;
  border-bottom: 1px solid #deded7;
  border-left: 0;
}

.massage-popover__section {
  padding: 8px;
  border: 1px solid #ededdf;
  border-radius: 6px;
  background: #faf9f2;
}

.massage-popover__section + .massage-popover__section {
  margin-top: 8px;
}

.massage-popover__source-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5px;
  gap: 8px;
}

.massage-popover__source {
  color: #77715f;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.massage-popover__room {
  margin-bottom: 7px;
  color: #252525;
  font-size: 14px;
  font-weight: 700;
}

.massage-popover__couple {
  display: inline-flex;
  padding: 2px 6px;
  border: 1px solid #7b6ee6;
  border-radius: 5px;
  background: #eeeafc;
  color: #5d50cb;
  font-size: 11px;
  font-weight: 700;
}

.massage-popover__guest {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr) auto;
  align-items: center;
  gap: 6px;
  min-height: 24px;
}

.massage-popover__guest + .massage-popover__guest {
  margin-top: 5px;
}

.massage-popover__guest--walk-in {
  margin-bottom: 8px;
}

.massage-popover__icon {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #3e96d8;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
}

.massage-popover__icon.massage-calendar__guest--female {
  background: #d84e7c;
}

.massage-popover__icon.massage-calendar__guest--free {
  background: #bcbcbc;
}

.massage-popover__icon img {
  width: 12px;
  height: 12px;
  filter: brightness(0) invert(1);
}

.massage-popover__guest-name {
  overflow: hidden;
  color: #252525;
  font-size: 13px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.massage-popover__info {
  padding: 2px 5px;
  border-radius: 4px;
  background: #fff;
  color: #77715f;
  font-size: 11px;
  font-weight: 600;
}

.massage-popover__meta {
  display: grid;
  gap: 4px;
  color: #555;
  font-size: 12px;
}

.massage-popover__clock {
  cursor: default;
}

.massage-popover__empty-title {
  color: #252525;
  font-size: 14px;
  font-weight: 700;
}

.massage-popover__empty-note {
  margin-top: 4px;
  color: #77715f;
  font-size: 12px;
}

.massage-popover__block-notes {
  margin-top: 4px;
  color: #252525;
  font-size: 13px;
  line-height: 1.35;
  white-space: pre-wrap;
}

.massage-popover__action {
  width: 100%;
  height: 32px;
  margin-top: 8px;
  border: 1px solid #77b017;
  border-radius: 6px;
  background: #77b017;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.massage-popover__action:hover {
  background: #6a9f14;
}

.massage-popover__action--danger {
  border-color: #4b4b4b;
  background: #4b4b4b;
}

.massage-popover__action--danger:hover {
  background: #333;
}

.massage-legend {
  display: flex;
  align-items: center;
  gap: 28px;
  padding: 9px 14px 8px;
  color: #3c3c3c;
  font-size: 16px;
  font-weight: 600;
}

.massage-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.massage-legend__empty {
  display: inline-flex;
  cursor: default;
}

.massage-legend__blocked {
  display: inline-flex;
  cursor: default;
}

.massage-dialog {
  width: 100%;
  max-height: calc(100vh - 80px);
  overflow: auto;
  background: #fff;
}

.massage-dialog__context {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
}

.massage-dialog__context-item {
  border: 1px solid #ececec;
  border-radius: 6px;
  padding: 8px 10px;
  background: #fafafa;
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.massage-dialog__context-item span {
  color: #6f6f6f;
}

.massage-dialog__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
}

.massage-segment {
  display: inline-flex;
  width: 100%;
  border: 1px solid #d7d7d7;
  border-radius: 6px;
  overflow: hidden;
}

.massage-segment__btn {
  flex: 1;
  height: 36px;
  background: #fff;
  border: 0;
  font-size: 14px;
  font-weight: 600;
  color: #555;
  cursor: pointer;
}

.massage-segment__btn + .massage-segment__btn {
  border-left: 1px solid #d7d7d7;
}

.massage-segment__btn--active {
  background: #77b017;
  color: #fff;
}

.massage-couple-switch {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 38px;
}

.massage-participant__source-row {
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 12px;
}

.massage-participant {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  background: #fafafa;
}

.massage-participant__title {
  margin: 0 0 10px;
  font-size: 15px;
}

.massage-dialog__actions {
  padding: 12px 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

</style>
